import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type PageHeaderProps = {
  /**
   * Main page title.
   */
  title: string;

  /**
   * Short page summary.
   */
  description?: string;

  /**
   * Action buttons or links displayed beside the title.
   */
  actions?: React.ReactNode;

  className?: string;
};

/**
 * A page title area with supporting text and optional right-aligned actions. Use near the
 * top of full pages and major page sections.
 *
 * @example
 * <Sdk.Components.PageHeader title="Students" description="Manage student records" actions={<Sdk.Components.Button>Add student</Sdk.Components.Button>} />
 */
export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)}
    >
      <div className="min-w-0 space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight text-balance text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="text-sm text-pretty text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}
